import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Profile, updateProfile } from '@/lib/forward-api';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProfileMessagesTabProps {
    profile: Profile;
    onUpdate: () => void;
}

export function ProfileMessagesTab({ profile, onUpdate }: ProfileMessagesTabProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [editing, setEditing] = useState(false);
    const [messages, setMessages] = useState({
        active: profile.messageActive,
        inactive: profile.messageInactive,
        failed: profile.messageFailed || 'ABC'
    });

    const handleSave = async () => {
        try {
            await updateProfile(profile.id, {
                messageActive: messages.active,
                messageInactive: messages.inactive
            });
            toast({
                title: t('success.title'),
                description: t('forward.messages.messagesUpdated')
            });
            setEditing(false);
            onUpdate();
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.messages.messagesUpdateError'),
                variant: 'destructive'
            });
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-gray-600">
                {t('forward.messages.description')}
            </p>

            <div className="space-y-4">
                <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium">
                        <ChevronRight className="h-4 w-4" />
                        {t('forward.messages.whenActive')}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                        <Textarea
                            value={messages.active}
                            onChange={(e) => setMessages({ ...messages, active: e.target.value })}
                            disabled={!editing}
                            rows={2}
                            className="resize-none"
                        />
                    </CollapsibleContent>
                </Collapsible>

                <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium">
                        <ChevronRight className="h-4 w-4" />
                        {t('forward.messages.whenInactive')}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                        <Textarea
                            value={messages.inactive}
                            onChange={(e) => setMessages({ ...messages, inactive: e.target.value })}
                            disabled={!editing}
                            rows={4}
                            className="resize-none"
                        />
                    </CollapsibleContent>
                </Collapsible>

                <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium">
                        <ChevronRight className="h-4 w-4" />
                        {t('forward.messages.whenFailed')}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                        <Textarea
                            value={messages.failed}
                            onChange={(e) => setMessages({ ...messages, failed: e.target.value })}
                            disabled={!editing}
                            rows={1}
                            className="resize-none"
                        />
                    </CollapsibleContent>
                </Collapsible>
            </div>

            <div className="flex justify-end gap-2 pt-4">
                {editing ? (
                    <>
                        <Button variant="outline" onClick={() => setEditing(false)}>
                            {t('global.button.cancel')}
                        </Button>
                        <Button onClick={handleSave}>
                            {t('global.button.save')}
                        </Button>
                    </>
                ) : (
                    <Button onClick={() => setEditing(true)}>
                        {t('global.button.edit')}
                    </Button>
                )}
            </div>
        </div>
    );
}
